import { RotateCcw } from "lucide-react";
import { Button } from "@eduviaos/ui-kit";
import type { ActivityDay } from "../../lib/learning/weekActivity";

export function WeekActivityChart({
  title,
  days,
  emptyLabel,
  errorLabel,
  retryLabel,
  truncatedLabel,
  status,
  truncated,
  onRetry,
}: {
  title: string;
  days: ActivityDay[];
  emptyLabel: string;
  errorLabel: string;
  retryLabel: string;
  truncatedLabel: string;
  status: "loading" | "error" | "empty" | "ready";
  truncated: boolean;
  onRetry: () => void;
}) {
  const peak = Math.max(1, ...days.map((day) => day.count));
  const summary = days.map((day) => `${day.label} ${day.count}`).join(", ");

  return (
    <section aria-label={title} className="space-y-3">
      <h2 className="font-display text-base font-semibold">{title}</h2>
      {status === "loading" ? (
        <div aria-busy="true" aria-label={title} className="flex h-24 items-end gap-2">
          {Array.from({ length: 7 }).map((_, index) => (
            <div
              key={index}
              className="bg-muted h-10 flex-1 animate-pulse rounded-md motion-reduce:animate-none"
            />
          ))}
        </div>
      ) : null}
      {status === "error" ? (
        <div role="alert" className="flex flex-col items-start gap-2">
          <p className="text-muted-foreground text-sm">{errorLabel}</p>
          <Button type="button" variant="outline" size="sm" onClick={onRetry}>
            <RotateCcw className="size-4" aria-hidden="true" />
            {retryLabel}
          </Button>
        </div>
      ) : null}
      {status === "empty" ? <p className="text-muted-foreground text-sm">{emptyLabel}</p> : null}
      {status === "ready" ? (
        <div className="space-y-2">
          <div role="img" aria-label={summary} className="flex h-24 items-end gap-2">
            {days.map((day) => (
              <div key={day.key} className="flex min-w-0 flex-1 flex-col items-center gap-1">
                <div className="flex h-16 w-full items-end">
                  <div
                    className={
                      day.count === 0
                        ? "bg-muted w-full rounded-md"
                        : "bg-primary w-full rounded-md"
                    }
                    style={{
                      height: day.count === 0 ? "2px" : `${Math.round((day.count / peak) * 100)}%`,
                    }}
                  />
                </div>
                <span className="text-muted-foreground text-[0.65rem] tabular-nums">
                  {day.label}
                </span>
                <span className="text-foreground text-[0.65rem] font-medium tabular-nums">
                  {day.count}
                </span>
              </div>
            ))}
          </div>
          {truncated ? <p className="text-muted-foreground text-xs">{truncatedLabel}</p> : null}
        </div>
      ) : null}
    </section>
  );
}
